import { PostPositionConfigurationRequest } from '@/shared/types/positionV2/api/post-position-configuration';
import { DealLineItem, DISPOSAL_PURPOSE, FREIGHT_TYPE, PROCESSING_TYPE } from '@schuettflix/interfaces';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { matchPricingStrategyByDealPricesAndProductType } from './matchPricingStrategyByDealPricesAndProductType';
import { productTypeToFreightTypeMap } from '@/shared/domain/product/productTypeToFreightTypeMap.ts';

type ProductConfiguration = Omit<
    PostPositionConfigurationRequest['productConfigurations'][number],
    'index' | 'isMainProduct' | 'additionalProperties'
> & {
    additionalProperties: {
        name: string;
        freightType?: FREIGHT_TYPE;
        disposalPurpose?: DISPOSAL_PURPOSE;
    };
};

function getProductConfigurationsFromDealLineItem({
    dealLineItem,
    lineItemIdsWithDeselectedRemarks,
    organizations,
    defaultTaxClassId,
    processingType,
}: {
    dealLineItem: DealLineItem;
    lineItemIdsWithDeselectedRemarks: string[];
    processingType: PROCESSING_TYPE;
    defaultTaxClassId: string;
    organizations: {
        salesforceAccountId: string;
        organization: {
            name: string;
            id: number;
        } | null;
    }[];
}): Array<ProductConfiguration> {
    const supplierOrganizationId = organizations.find(
        ({ salesforceAccountId }) => dealLineItem.salesforceMetaData.supplierAccountId === salesforceAccountId
    )?.organization?.id;
    const carrierOrganizationId = organizations.find(
        ({ salesforceAccountId }) => dealLineItem.salesforceMetaData.carrierAccountId === salesforceAccountId
    )?.organization?.id;

    const partnerOrganizationId = supplierOrganizationId || carrierOrganizationId;

    if (!partnerOrganizationId) {
        throw new Error(
            `Deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId} is missing required partner organization mapping`
        );
    }

    if (!dealLineItem.currencyCode) {
        throw new Error(
            `Deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId} is missing required currencyCode mapping`
        );
    }

    if (!dealLineItem.product.productType) {
        throw new Error(
            `Deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId} has no product type assigned`
        );
    }
    const productType = dealLineItem.product.productType;

    let productName = `${dealLineItem.product.name}`;
    const lineItemShouldShowRemark = !lineItemIdsWithDeselectedRemarks.includes(
        dealLineItem.salesforceMetaData.opportunityLineItemId
    );
    if (lineItemShouldShowRemark && dealLineItem.remark) {
        productName = `${productName}, ${dealLineItem.remark}`;
    }

    const dealLineItemPricingStrategy = matchPricingStrategyByDealPricesAndProductType(dealLineItem, productType);
    if (!dealLineItemPricingStrategy) {
        throw new Error(
            `Could not determine pricing strategy for deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId}`
        );
    }

    const mainProductConfiguration: ProductConfiguration = {
        productType,
        category: getProductCategoryByProductType(productType),
        partnerOrganizationId: partnerOrganizationId,
        purchasePrice: dealLineItem.materialPrice ?? dealLineItem.transportPrice ?? 0,
        salesPrice: dealLineItem.salesPrice,
        productName,
        quantity: dealLineItem.quota,
        unit: dealLineItem.unit,
        salesQuantity: dealLineItem.quota,
        salesUnit: dealLineItem.unit,
        purchaseQuantity: dealLineItem.quota,
        purchaseUnit: dealLineItem.unit,
        purchaseTaxClassId: defaultTaxClassId,
        salesTaxClassId: defaultTaxClassId,
        currencyCode: dealLineItem.currencyCode,
        showZeroPurchasePrice: false,
        showZeroSalesPrice: false,
        processingType: processingType,
        salesforceOpportunityLineItemId: dealLineItem.salesforceMetaData.opportunityLineItemId,
        additionalProperties: {
            name: productName,
            freightType: dealLineItem.product.freightType ?? undefined,
            ...(productType.includes('DISPOSAL')
                ? {
                      disposalPurpose: 'DISPOSAL',
                  }
                : {}),
            ...(productType.includes('RECYCLING')
                ? {
                      disposalPurpose: 'RECYCLING',
                  }
                : {}),
        },
    };

    if (dealLineItemPricingStrategy === 'STANDARD') {
        return [mainProductConfiguration];
    }

    const materialPrice = dealLineItem.materialPrice ?? dealLineItem.francoPrice;
    const supplierPartnerOrganizationId = supplierOrganizationId ?? carrierOrganizationId;
    const carrierPartnerOrganizationId = carrierOrganizationId ?? supplierOrganizationId;
    if (!supplierPartnerOrganizationId || !carrierPartnerOrganizationId) {
        throw new Error(
            `Deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId} is missing required partner organization mapping`
        );
    }
    if (typeof materialPrice !== 'number') {
        throw new Error(
            `Deal line item with id ${dealLineItem.salesforceMetaData.opportunityLineItemId} is missing required materialPrice mapping`
        );
    }

    const materialProductConfiguration: ProductConfiguration = {
        ...mainProductConfiguration,
        partnerOrganizationId: supplierPartnerOrganizationId,
        purchasePrice: materialPrice,
    };

    const transportProductType = processingType === 'AUTOMATIC' ? 'TRANSPORT' : 'CUSTOM_TRANSPORT';
    const transportProductConfiguration: ProductConfiguration = {
        ...materialProductConfiguration,
        productType: transportProductType,
        category: getProductCategoryByProductType(transportProductType),
        partnerOrganizationId: carrierPartnerOrganizationId,
        purchasePrice: dealLineItem.transportPrice ?? 0,
        salesPrice: 0,
        additionalProperties: {
            ...materialProductConfiguration.additionalProperties,
            freightType:
                dealLineItem.product.freightType ??
                productTypeToFreightTypeMap[materialProductConfiguration.productType],
        },
    };

    return [materialProductConfiguration, transportProductConfiguration];
}

export function getProductConfigurationsFromDealLineItems(selection: {
    dealLineItems: DealLineItem[];
    lineItemIdsWithDeselectedRemarks: string[];
    processingType: PROCESSING_TYPE;
    defaultTaxClassId: string;
    organizations: {
        salesforceAccountId: string;
        organization: {
            name: string;
            id: number;
        } | null;
    }[];
}): Array<ProductConfiguration & { isMainProduct: boolean; index: number }> {
    const productConfigurations = selection.dealLineItems.flatMap(dealLineItem => {
        return getProductConfigurationsFromDealLineItem({
            ...selection,
            dealLineItem,
        });
    });

    const materials = productConfigurations.filter(p => p.category === 'MATERIAL');
    const transports = productConfigurations
        .filter(p => p.category === 'TRANSPORT')
        .map(p => {
            const mainMaterialProduct = materials[0];
            if (!mainMaterialProduct) {
                return p;
            }
            return {
                ...p,
                additionalProperties: {
                    ...p.additionalProperties,
                    freightType: productTypeToFreightTypeMap[mainMaterialProduct.productType],
                    disposalPurpose: mainMaterialProduct.additionalProperties.disposalPurpose,
                },
            };
        });
    const materialServices = productConfigurations.filter(p => p.category === 'MATERIAL_SERVICE');
    const transportServices = productConfigurations.filter(p => p.category === 'TRANSPORT_SERVICE');

    let sortedConfigurations: ProductConfiguration[];
    if (materials.length > 0 && transports.length > 0) {
        sortedConfigurations = [
            materials[0],
            transports[0],
            ...materials.slice(1),
            ...transports.slice(1),
            ...materialServices,
            ...transportServices,
        ];
    } else {
        sortedConfigurations = [...materials, ...transports, ...materialServices, ...transportServices];
    }

    return sortedConfigurations.map((productConfiguration, index) => ({
        ...productConfiguration,
        isMainProduct: index === 0,
        index,
    }));
}
